/**
 * Historical FX rates for months the app has none.
 *
 * WHY THIS EXISTS
 * `fxEffectOnCost` can only price a purchase at the rate of its own month, and `monthlyRates`
 * only goes back as far as the owner's first tracked month. Live, that left the SAP plan's
 * five 2025 lots — $7,572 of cost basis, 12% of the total — permanently uncomputable, not
 * because the dates were missing but because the RATES were. A date with no rate and no date
 * at all are equally unanswerable, and both were being reported as such. This closes the
 * second one.
 *
 * IT ONLY EVER FILLS HOLES. An existing rate is never overwritten: the reconciled months
 * carry the rates the owner actually transacted at, and a vendor's month-end close is not a
 * better version of that — it is a different number for a different question. Same rule as
 * benchmark-history: months are written once and never refetched, because a historical close
 * does not change.
 *
 * TRIGGERED BY THE USER, NEVER BY A RENDER, for the same reason as every other fetch in this
 * codebase: a render path that reaches the network is how surprise cloud writes happen.
 *
 * SIZE. Two floats per month. Five years of both pairs is under 2 KB — far below the
 * no-Supabase-bloat line.
 */

/**
 * Troy ounce in grams. The app quotes metals per GRAM (prices.js converts every fetched
 * USD/oz quote with this exact divisor) while every upstream series quotes per ounce, so the
 * two must not drift apart — a metal history stored per ounce beside holdings measured in
 * grams would be wrong by 31x and look plausible.
 */
const OZ_G = 31.1035;

/** Months that carry a usable USD/TRY rate today. */
export function ratedMonths(S) {
  const out = new Set();
  Object.entries((S && S.monthlyRates) || {}).forEach(([m, r]) => {
    if (r && r.usdtry > 0) out.add(m);
  });
  return out;
}

/**
 * Months some dated cost sits in but NO usable USD/TRY rate covers. This is the hard gate:
 * without it the month cannot be priced at all.
 * @param {string[]} months  YYYY-MM strings the caller needs priced
 */
export function missingRateMonths(S, months) {
  const have = ratedMonths(S);
  return [...new Set((months || []).filter(m => /^\d{4}-\d{2}$/.test(m) && !have.has(m)))].sort();
}

/**
 * Months missing ANY of the four rates. Broader than the gate above, and the right input to
 * a backfill: a month can carry USD/TRY and still be unable to price a gram of gold, which
 * is exactly the state the reserve fund's 15.09g XAU and 111.27g XAG were left in — dated,
 * rated for currency, and still uncomputable.
 */
export function incompleteRateMonths(S, months) {
  const mr = (S && S.monthlyRates) || {};
  return [...new Set((months || []).filter(m => {
    if (!/^\d{4}-\d{2}$/.test(m)) return false;
    const r = mr[m];
    return !r || !(r.usdtry > 0) || !(r.eurtry > 0) || !(r.xauUSD > 0) || !(r.xagUSD > 0);
  }))].sort();
}

/**
 * Fetch month-end closes for USD/TRY and EUR/TRY and fill only the gaps.
 *
 * Returns a report, not a boolean — "nothing was missing" and "the fetch died" must never
 * look the same to the caller.
 *
 * @returns {Promise<{ok:boolean, added:number, months:string[], stillMissing:string[], error?:string}>}
 */
export async function backfillMonthlyRates(S, months, fetchImpl) {
  const want = incompleteRateMonths(S, months);
  if (!want.length) return { ok: true, added: 0, months: [], stillMissing: [] };

  const doFetch = fetchImpl || (url => fetch(url).then(r => {
    if (!r.ok) throw new Error('HTTP ' + r.status);
    return r.json();
  }));

  let payload;
  try {
    payload = await doFetch('/api/ta?mode=monthly&tickers=USDTRY,EURTRY,XAU,XAG&range=10y');
  } catch (e) {
    return { ok: false, added: 0, months: [], stillMissing: want, error: (e && e.message) || 'fetch failed' };
  }

  const usd = payload && payload.monthly && payload.monthly.USDTRY;
  const eur = payload && payload.monthly && payload.monthly.EURTRY;
  const xau = payload && payload.monthly && payload.monthly.XAU;   // USD per OUNCE upstream
  const xag = payload && payload.monthly && payload.monthly.XAG;
  if (!usd || typeof usd !== 'object') {
    return { ok: false, added: 0, months: [], stillMissing: want, error: 'no USDTRY series returned' };
  }

  if (!S.monthlyRates) S.monthlyRates = {};
  const added = [];
  want.forEach(m => {
    const u = Number(usd[m]);
    const row0 = S.monthlyRates[m];
    // A month that already has its currency rate can still be missing metals; the currency
    // rate simply is not the thing to gate on for those.
    if (!(u > 0) && !(row0 && row0.usdtry > 0)) return;
    // A month created here has no purchases in it by definition — it exists only to carry a
    // rate. `status: 'legacy'` is what the rest of the app already uses for a month with no
    // reconciled entries, so nothing downstream has to learn a new state.
    const row = S.monthlyRates[m] || (S.monthlyRates[m] = { status: 'legacy', entries: {} });
    if (!(row.usdtry > 0) && u > 0) row.usdtry = +u.toFixed(4);
    const e = Number(eur && eur[m]);
    if (e > 0 && !(row.eurtry > 0)) row.eurtry = +e.toFixed(4);
    // Metals, stored as USD PER GRAM. Per gram because that is the unit the holdings and the
    // reserve fund are measured in; in USD rather than TRY so the value does not depend on
    // this same row's usdtry and cannot silently inherit an error from it.
    const au = Number(xau && xau[m]);
    if (au > 0 && !(row.xauUSD > 0)) row.xauUSD = +(au / OZ_G).toFixed(6);
    const ag = Number(xag && xag[m]);
    if (ag > 0 && !(row.xagUSD > 0)) row.xagUSD = +(ag / OZ_G).toFixed(6);
    added.push(m);
  });

  // stillMissing reports the HARD gate only. A month that gained a currency rate but whose
  // metal series has no bucket upstream is not a failure to report every time the button is
  // pressed — it is simply the edge of what exists.
  return { ok: true, added: added.length, months: added, stillMissing: missingRateMonths(S, months) };
}

/**
 * Every month any dated cost lump falls in — the input to the two functions above.
 * Takes the output of `datedCostBasis` so this module needs no opinion about where cost
 * comes from, and cannot drift from the module that decides.
 */
export function monthsOfCost(datedItems) {
  return [...new Set((datedItems || [])
    .map(it => String((it && it.date) || '').slice(0, 7))
    .filter(m => /^\d{4}-\d{2}$/.test(m)))].sort();
}
